import { Link } from "react-router-dom";
import { Lock, Mountain } from "lucide-react";

/**
 * LockedOverlay — semi-transparent gold overlay shown on top of locked content.
 * Used on locked activities, locked stages, and locked features.
 *
 * Props:
 *  - title: optional short title above message
 *  - message: the encouraging copy
 *  - ctaLabel (default "Upgrade Now")
 *  - to (default "/pricing") — where the CTA navigates
 *  - icon — lucide icon component (default Lock)
 *  - testid — prefix for data-testid attributes
 *  - compact — smaller padding for cards
 */
export const LockedOverlay = ({
  title,
  message,
  ctaLabel = "Upgrade Now",
  to = "/pricing",
  icon: Icon = Lock,
  testid = "locked-overlay",
  compact = false,
  footnote,
}) => (
  <div
    className={`absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[1.5px] rounded-2xl`}
    data-testid={testid}
    style={{
      background: "linear-gradient(135deg, rgba(212,168,67,0.35) 0%, rgba(27,58,92,0.55) 100%)",
    }}
  >
    <div className={`bg-mm-parchment border-2 border-mm-gold rounded-2xl shadow-xl text-center ${compact ? "p-5 max-w-sm" : "p-7 max-w-md"} mx-3`}>
      <div className="w-12 h-12 mx-auto rounded-full bg-mm-gold/20 text-mm-navy flex items-center justify-center mb-3">
        <Icon size={22} strokeWidth={2.5} />
      </div>
      {title && (
        <div className="font-heading font-black text-mm-navy text-lg mb-2" data-testid={`${testid}-title`}>
          {title}
        </div>
      )}
      <p className="text-mm-navy/85 text-sm leading-relaxed" data-testid={`${testid}-message`}>
        {message}
      </p>
      <Link
        to={to}
        className="btn-trail btn-gold text-sm mt-4 inline-flex"
        data-testid={`${testid}-cta`}
      >
        {ctaLabel}
      </Link>
      {footnote && (
        <div className="mt-3 text-[11px] text-mm-navy/60" data-testid={`${testid}-footnote`}>
          {footnote}
        </div>
      )}
    </div>
  </div>
);

/**
 * UpgradePrompt — inline (non-overlay) encouraging card. Used on Badges,
 * Leaderboard toggle, and other spots where a gentle nudge fits better.
 */
export const UpgradePrompt = ({ message, ctaLabel = "Upgrade Now", to = "/pricing", testid = "upgrade-prompt", icon: Icon = Mountain }) => (
  <div
    className="rounded-xl border-2 border-mm-gold/40 bg-mm-gold/10 p-4 flex items-start gap-3"
    data-testid={testid}
  >
    <Icon size={18} strokeWidth={2.5} className="text-mm-navy mt-0.5 shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-sm text-mm-navy/90" data-testid={`${testid}-message`}>{message}</p>
      <Link to={to} className="text-sm font-bold text-mm-navy underline hover:text-mm-steel mt-1 inline-block" data-testid={`${testid}-cta`}>
        {ctaLabel} →
      </Link>
    </div>
  </div>
);

export default LockedOverlay;
